// src/services/clientService.js
import api from '../api/axios';

/**
 * IMPORTANT
 * - Default endpoints assumed:
 *    GET    /clients
 *    GET    /clients/:id
 *    POST   /clients
 *    PUT    /clients/:id
 *    PATCH  /clients/:id
 *    DELETE /clients/:id
 * - If PATCH is not implemented by your backend, we fall back to PUT,
 *   then to legacy PUT /clients/update/:id.
 */

/* --------------------------- error utilities --------------------------- */

function normalizeError(err, fallback = 'Request failed') {
  const status = err?.response?.status;
  const dataMessage =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback;

  const error = new Error(dataMessage);
  error.status = status;
  error.raw = err;
  return error;
}

/* ------------------------------ constants ------------------------------ */

// Which fields can be edited inline in the list view.
// Keep this in sync with your UX decisions.
export const INLINE_EDITABLE_FIELDS = new Set([
  'name',
  'email',
  'phone',
  'company',
  'status',
  'tags',    // expected to be string[] or comma-separated string from UI
  'website',
]);

/* ------------------------------- services ------------------------------ */

/** List clients with optional query params */
export async function listClients(params = {}) {
  try {
    const { data } = await api.get('/clients', { params });
    return data; // array or {items,total} depending on backend
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch clients');
  }
}

/** Fetch single client by id */
export async function getClientById(id) {
  try {
    const { data } = await api.get(`/clients/${id}`);
    return data;
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch client');
  }
}

/** Create a new client */
export async function createClient(payload) {
  try {
    const { data } = await api.post('/clients', payload);
    return data;
  } catch (err) {
    throw normalizeError(err, 'Failed to create client');
  }
}

/** Replace entire client (rare; prefer patch) */
export async function updateClient(id, payload) {
  try {
    const { data } = await api.put(`/clients/${id}`, payload);
    return data;
  } catch (err) {
    throw normalizeError(err, 'Failed to update client');
  }
}

/**
 * Patch client with partial fields.
 * Falls back to PUT and then legacy PUT /clients/update/:id for backends
 * that don’t support PATCH.
 */
export async function patchClient(id, partial) {
  try {
    const { data } = await api.patch(`/clients/${id}`, partial);
    return data;
  } catch (err) {
    const code = err?.response?.status;
    // 404/405 are common when PATCH route/verb isn't available
    if (code === 404 || code === 405) {
      try {
        const { data } = await api.put(`/clients/${id}`, partial);
        return data;
      } catch (err2) {
        const code2 = err2?.response?.status;
        if (code2 === 404) {
          // legacy fallback
          const { data } = await api.put(`/clients/update/${id}`, partial);
          return data;
        }
        throw normalizeError(err2, 'Failed to update client');
      }
    }
    throw normalizeError(err, 'Failed to update client');
  }
}

/**
 * Inline field update helper.
 * Enforces a whitelist so your UI can’t accidentally send unsupported keys.
 * Converts comma-separated tag string to array automatically.
 * Uses the same PATCH→PUT→legacy fallback chain as patchClient.
 */
export async function updateClientField(id, field, value) {
  if (!INLINE_EDITABLE_FIELDS.has(field)) {
    throw new Error(`Field "${field}" is not allowed for inline editing`);
  }

  // Normalize tags from "tag1, tag2" -> ["tag1","tag2"]
  let payloadValue = value;
  if (field === 'tags') {
    if (Array.isArray(value)) {
      payloadValue = value;
    } else if (typeof value === 'string') {
      payloadValue = value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (value == null) {
      payloadValue = [];
    }
  }

  return patchClient(id, { [field]: payloadValue });
}

/** Delete a client */
export async function deleteClient(id) {
  try {
    const { data } = await api.delete(`/clients/${id}`);
    return data;
  } catch (err) {
    throw normalizeError(err, 'Failed to delete client');
  }
}

/** Bulk delete (best-effort) */
export async function bulkDeleteClients(ids = []) {
  if (!ids.length) return { deleted: 0 };
  let deleted = 0;
  const errors = [];

  await Promise.all(
    ids.map(async (id) => {
      try {
        await api.delete(`/clients/${id}`);
        deleted += 1;
      } catch (err) {
        errors.push({ id, error: normalizeError(err).message });
      }
    })
  );

  return { deleted, errors };
}

/**
 * Export filtered list as CSV (server-side).
 * If your backend doesn’t support it, keep your current client-side export.
 */
export async function exportClientsCSV(params = {}) {
  try {
    const res = await api.get('/clients/export', {
      params,
      responseType: 'blob',
    });
    return res.data; // Blob
  } catch (err) {
    throw normalizeError(err, 'Failed to export clients');
  }
}

/* ------------------------------ convenience ------------------------------ */

export async function saveClient(payload) {
  const id = payload?._id || payload?.id;
  if (id) return updateClient(id, payload);
  return createClient(payload);
}

export default {
  listClients,
  getClientById,
  createClient,
  updateClient,
  patchClient,
  updateClientField,
  deleteClient,
  bulkDeleteClients,
  exportClientsCSV,
  saveClient,
  INLINE_EDITABLE_FIELDS,
};
